import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { RichTextContent } from "@/shared/components/rich-text/rich-text-content";
import { formatTerminDate } from "@/shared/format/termin-date";
import { useGsapParallax } from "@/shared/hooks/use-gsap-parallax";
import { useGsapReveal } from "@/shared/hooks/use-gsap-reveal";
import { isBlankHtml, stripHtml } from "@/shared/lib/html";
import { usePageMeta } from "@/shared/seo/use-page-meta";
import { Link } from "@tanstack/react-router";
import { formatDelkaDnu } from "@template/shared";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  HelpCircle,
  MapPin,
  Phone,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { HCA_PHONE_DISPLAY, HCA_PHONE_TEL, usePublicActions } from "./public-actions";

interface ProduktLandingPageProps {
  slug: string;
}

const CURRENCY_FMT = new Intl.NumberFormat("cs-CZ", {
  style: "currency",
  currency: "CZK",
  maximumFractionDigits: 0,
});

/** Výchozí místo konání, když produkt/termín nemá vlastní. */
const DEFAULT_MISTO = "Úlehlova 20a, Brno";

/** Převede peněžní string na kladné číslo, jinak null. Ošetřuje "", "0.00". */
function toAmount(value: string | null | undefined): number | null {
  if (value == null || value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Vrátí oříznutý neprázdný string, jinak fallback. Ošetřuje prázdný řetězec. */
function nonEmpty(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

const KATEGORIE_LABEL: Record<string, string> = {
  komunikace: "Komunikace",
  obchod: "Obchod",
  leadership: "Leadership",
  management: "Management",
  ostatni: "Rozvoj",
};

interface CategoryContent {
  audience: string;
  outcomes: readonly string[];
}

/** Fallback obsah pro neznámou kategorii (typově nepodmíněný). */
const FALLBACK_CONTENT: CategoryContent = {
  audience:
    "Pro každého, kdo chce růst v praktických dovednostech a posunout výkon svůj i svého týmu.",
  outcomes: [
    "Praktické dovednosti využitelné hned",
    "Práce s reálnými situacemi z vašeho prostředí",
    "Individuální přístup v malé skupině",
    "Podpora i po skončení semináře",
  ],
};

/** Obsah „pro koho" + „co si odnesete" podle kategorie produktu. */
const CATEGORY_CONTENT: Record<string, CategoryContent> = {
  management: {
    audience:
      "Pro vedoucí pracovníky, team leadery a manažery, kteří chtějí vést svůj tým s vyšší produktivitou a menším chaosem.",
    outcomes: [
      "Konkrétní nástroje pro zvýšení výkonu týmu bez mikromanagementu",
      "Jak nastavit priority, delegovat a získat zpět čas na řízení",
      "Méně hašení požárů, více systematického vedení",
      "Postupy, které zavedete hned druhý den",
    ],
  },
  leadership: {
    audience:
      "Pro majitele, jednatele a manažery, kteří chtějí vést přirozenou autoritou a budovat tým, který táhne za jeden provaz.",
    outcomes: [
      "Jak vést lidi tak, aby chtěli, ne museli",
      "Budování důvěry a zdravé firemní kultury",
      "Zvládání náročných rozhovorů a konfliktů",
      "Rozvoj sebe i svých lidí",
    ],
  },
  obchod: {
    audience:
      "Pro obchodníky, obchodní manažery a majitele, kteří chtějí prodávat více a s jistotou.",
    outcomes: [
      "Vedení obchodního rozhovoru od prvního kontaktu po uzavření",
      "Práce s námitkami a vyjednávání o ceně",
      "Budování dlouhodobých vztahů s klienty",
      "Konkrétní techniky ověřené v praxi",
    ],
  },
  komunikace: {
    audience:
      "Pro všechny, kdo chtějí komunikovat jasně, asertivně a se skutečným dopadem — ve firmě i navenek.",
    outcomes: [
      "Srozumitelná a sebevědomá komunikace",
      "Asertivní zvládání náročných situací",
      "Jak předávat zpětnou vazbu, která funguje",
      "Prezentační a vyjednávací dovednosti",
    ],
  },
  ostatni: FALLBACK_CONTENT,
};

/** Jak seminář probíhá — společné pro všechny produkty (přístup HCA). */
const HOW_IT_WORKS: ReadonlyArray<{ title: string; text: string }> = [
  {
    title: "Praxe, ne přednáška",
    text: "Trénujete na reálných situacích z vaší firmy, ne na obecné teorii.",
  },
  {
    title: "Malá skupina",
    text: "Pracujeme v menších skupinách, aby měl každý prostor a pozornost lektora.",
  },
  {
    title: "Použitelné hned",
    text: "Odcházíte s konkrétními nástroji, které nasadíte druhý den v práci.",
  },
  {
    title: "Podpora i potom",
    text: "Po semináři zůstáváme k dispozici — pomůžeme s aplikací do praxe.",
  },
];

/** Proč HCA Moravia — proof points. */
const WHY_HCA: readonly string[] = [
  "Lektoři z reálné podnikové praxe, ne jen teoretici",
  "Stovky firem a tisíce vyškolených lidí",
  "Důraz na aplikaci do vašeho byznysu a měřitelný posun",
];

const FAQ: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: "Můžeme seminář uspořádat jen pro naši firmu?",
    a: "Ano. Kromě otevřených termínů vám seminář připravíme jako firemní (in-house) na míru vašemu týmu i tématu. Ozvěte se nám a domluvíme detaily.",
  },
  {
    q: "Jak probíhá přihlášení a fakturace?",
    a: "Vyberete termín a vyplníte nezávaznou přihlášku. Ozveme se vám, potvrdíme účast a teprve poté vystavíme fakturu se splatností. Platba probíhá převodem na základě faktury.",
  },
  {
    q: "Co když se na vybraný termín nedostavím?",
    a: "Domluvíme přesun na jiný termín nebo účast náhradníka z vaší firmy — stačí nám dát včas vědět.",
  },
  {
    q: "Kolik je ve skupině účastníků?",
    a: "Pracujeme záměrně v menších skupinách, aby měl každý dostatek prostoru k nácviku a individuální zpětnou vazbu od lektora.",
  },
];

/** Detail produktu + nejbližší termíny + CTA. */
export function ProduktLandingPage({ slug }: ProduktLandingPageProps) {
  const { openDiagnoza } = usePublicActions();
  const query = trpc.hca.products.getBySlug.useQuery({ slug }, { retry: false });

  // SEO — název + popis semináře; dokud se data načítají, platí brand defaulty.
  // Popis je HTML (rich text) → do meta description ho strippneme na plain text.
  // Neexistující slug = noindex (soft-404 nesmí mít indexovatelný canonical).
  usePageMeta({
    title: query.data?.nazev,
    description: query.data?.popis ? stripHtml(query.data.popis) : undefined,
    path: `/seminare/${slug}`,
    noindex: query.isError,
  });

  // Decentní scroll reveal — konzistentní s ostatními veřejnými stránkami.
  // Hodnoty (y≤24, stagger 0.08, start "top 85%") dle design review (Gemini):
  // prémiově střídmé, jednorázové (once:true), bez horizontálních posunů.
  // Hooky musí běžet bezpodmínečně před early-returny (rules of hooks).
  const detailsRef = useGsapReveal<HTMLElement>({
    childrenSelector: "[data-reveal]",
    stagger: 0.08,
    y: 20,
    start: "top 85%",
  });
  const valueRef = useGsapReveal<HTMLElement>({ y: 24, start: "top 85%" });
  const howRef = useGsapReveal<HTMLElement>({
    childrenSelector: "[data-reveal]",
    stagger: 0.08,
    y: 20,
    start: "top 85%",
  });
  const whyRef = useGsapReveal<HTMLElement>({ y: 24, start: "top 85%" });
  const upcomingRef = useGsapReveal<HTMLElement>({ y: 24, start: "top 85%" });
  const faqRef = useGsapReveal<HTMLElement>({ y: 20, start: "top 85%" });
  const heroGlowRef = useGsapParallax<HTMLDivElement>({ amount: 50 });

  if (query.isError) {
    return (
      <div
        className="mx-auto max-w-3xl px-4 py-16 text-center"
        data-testid="hca-produkt-landing-error"
      >
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" aria-hidden="true" />
        <h1 className="mb-2 text-2xl font-bold">Produkt nenalezen</h1>
        <p className="mb-6 text-muted-foreground">{query.error.message}</p>
        <Button variant="outline" asChild data-testid="hca-produkt-landing-error-back-button">
          <Link to="/terminy" data-testid="hca-produkt-landing-error-back">
            Zobrazit všechny termíny
          </Link>
        </Button>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div
        className="mx-auto max-w-3xl px-4 py-16 space-y-6"
        data-testid="hca-produkt-landing-skeleton"
      >
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const product = query.data;

  // Robustní odvození ceny a místa (ošetří "" a "0.00" z DB).
  const perUcastnik = toAmount(product.cenaPerUcastnik);
  const katalog = toAmount(product.cenaKatalog);
  const priceAmount = perUcastnik ?? katalog;
  const priceLabel = perUcastnik ? "za účastníka" : "cena semináře";
  const misto = nonEmpty(product.defaultMisto, DEFAULT_MISTO);
  const kategorieLabel = KATEGORIE_LABEL[product.kategorie] ?? product.kategorie;
  const content = CATEGORY_CONTENT[product.kategorie] ?? FALLBACK_CONTENT;

  return (
    <div className="flex flex-col" data-testid="hca-produkt-landing">
      <section
        className="relative isolate overflow-hidden bg-[color:var(--hca-navy)] px-4 py-16 text-[color:var(--hca-ivory)]"
        data-testid="hca-produkt-landing-hero"
      >
        {/* Decentní parallax glow — pohyb pomalejší než scroll dává hloubku. */}
        <div
          ref={heroGlowRef}
          aria-hidden="true"
          className="-z-10 pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full opacity-60"
          style={{
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--hca-gold) 22%, transparent) 0%, transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-6xl">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-[color:var(--hca-gold)]">
            {kategorieLabel}
          </div>
          <h1
            className="mb-4 text-4xl font-bold tracking-tight sm:text-5xl"
            data-testid="hca-produkt-landing-title"
          >
            {product.nazev}
          </h1>

          {/* Rychlý přehled v hero pro okamžitou skenovatelnost. */}
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[color:var(--hca-ivory)]/90">
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-[color:var(--hca-gold)]" aria-hidden="true" />
              {formatDelkaDnu(product.delkaDnu)}
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[color:var(--hca-gold)]" aria-hidden="true" />
              {misto}
            </span>
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-[color:var(--hca-gold)]" aria-hidden="true" />
              {priceAmount
                ? `${CURRENCY_FMT.format(priceAmount)} ${priceLabel} · bez DPH`
                : "Cena na vyžádání"}
            </span>
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" asChild data-testid="hca-produkt-landing-hero-prihlasit-button">
              <a href="#terminy">
                <Calendar className="h-4 w-4" aria-hidden="true" />
                Přihlásit se
              </a>
            </Button>
            <Button
              type="button"
              size="lg"
              variant="outline"
              className="border-white/35 bg-transparent text-white hover:bg-white/10 hover:text-white"
              onClick={openDiagnoza}
              data-testid="hca-produkt-landing-hero-diagnoza-button"
            >
              Spustit diagnostiku
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/35 bg-transparent text-white hover:bg-white/10 hover:text-white"
              asChild
            >
              <a href={`tel:${HCA_PHONE_TEL}`}>
                <Phone className="h-4 w-4" aria-hidden="true" />
                {HCA_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section
        ref={detailsRef}
        className="mx-auto w-full max-w-6xl px-4 py-12"
        data-testid="hca-produkt-landing-details"
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Card data-reveal>
            <CardContent className="flex items-start gap-3 p-4">
              <Clock className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Délka
                </div>
                <div className="font-semibold">{formatDelkaDnu(product.delkaDnu)}</div>
              </div>
            </CardContent>
          </Card>
          <Card data-reveal>
            <CardContent className="flex items-start gap-3 p-4">
              <Users className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Cena
                </div>
                <div className="font-semibold" data-testid="hca-produkt-landing-cena">
                  {priceAmount ? CURRENCY_FMT.format(priceAmount) : "Na vyžádání"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {priceAmount ? `${priceLabel} · bez DPH` : "ozvěte se nám"}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-reveal>
            <CardContent className="flex items-start gap-3 p-4">
              <MapPin className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Místo
                </div>
                <div className="font-semibold" data-testid="hca-produkt-landing-misto">
                  {misto}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pro koho + co si odnesete */}
      <section
        ref={valueRef}
        className="mx-auto w-full max-w-6xl px-4 pb-12"
        data-testid="hca-produkt-landing-value"
        aria-label="Pro koho je seminář a co si odnesete"
      >
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
              <Target className="h-4 w-4" aria-hidden="true" />
              Pro koho je seminář
            </div>
            <p className="text-muted-foreground">{content.audience}</p>
          </div>
          <div>
            <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              Co si odnesete
            </div>
            <ul className="space-y-2">
              {content.outcomes.map((item) => (
                <li key={item} className="flex items-start gap-2 text-muted-foreground">
                  <CheckCircle2
                    className="mt-0.5 h-5 w-5 shrink-0 text-primary"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* O semináři — formátovaný popis z administrace (rich text) */}
      {isBlankHtml(product.popis) ? null : (
        <section
          className="mx-auto w-full max-w-6xl px-4 pb-12"
          data-testid="hca-produkt-landing-about"
          aria-label="O semináři"
        >
          <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-primary">
            <HelpCircle className="h-4 w-4" aria-hidden="true" />O semináři
          </div>
          <RichTextContent
            html={product.popis ?? ""}
            className="max-w-3xl text-base text-muted-foreground leading-7"
            testId="hca-produkt-landing-popis"
          />
        </section>
      )}

      {/* Jak to probíhá */}
      <section
        ref={howRef}
        className="bg-muted/30 px-4 py-12"
        data-testid="hca-produkt-landing-how"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-6 text-2xl font-bold" data-reveal>
            Jak seminář probíhá
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {HOW_IT_WORKS.map((step) => (
              <Card key={step.title} data-reveal>
                <CardContent className="p-5">
                  <div className="mb-1 font-semibold">{step.title}</div>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Proč HCA */}
      <section
        ref={whyRef}
        className="mx-auto w-full max-w-6xl px-4 py-12"
        data-testid="hca-produkt-landing-why"
      >
        <h2 className="mb-6 text-2xl font-bold">Proč HCA Moravia</h2>
        <ul className="grid gap-4 sm:grid-cols-3">
          {WHY_HCA.map((item) => (
            <li key={item} className="flex items-start gap-2 text-muted-foreground">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Firemní seminář na míru (in-house) */}
      <section className="px-4 pb-12" data-testid="hca-produkt-landing-inhouse">
        <div className="mx-auto max-w-6xl rounded-xl border border-border bg-[color:var(--hca-navy)] px-6 py-8 text-[color:var(--hca-ivory)]">
          <div className="grid items-center gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <h2 className="text-2xl font-bold">Chcete tento seminář jen pro svůj tým?</h2>
              <p className="mt-2 max-w-2xl text-[color:var(--hca-ivory)]/80">
                Připravíme ho jako firemní (in-house) na míru — obsah, termín i příklady podle vaší
                situace. Napište nebo zavolejte a domluvíme se.
              </p>
            </div>
            <Button
              size="lg"
              variant="outline"
              className="border-white/35 bg-transparent text-white hover:bg-white/10 hover:text-white"
              asChild
              data-testid="hca-produkt-landing-inhouse-phone"
            >
              <a href={`tel:${HCA_PHONE_TEL}`}>
                <Phone className="h-4 w-4" aria-hidden="true" />
                {HCA_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section
        ref={upcomingRef}
        id="terminy"
        className="scroll-mt-20 bg-muted/30 px-4 py-12"
        data-testid="hca-produkt-landing-upcoming"
        aria-label="Nadcházející termíny"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold">Nejbližší termíny</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Přihláška je nezávazná a váže se ke konkrétnímu termínu; po odeslání se vám ozveme a
              potvrdíme účast.
            </p>
          </div>
          {product.upcomingTerminy.length === 0 ? (
            <Card data-testid="hca-produkt-landing-upcoming-empty">
              <CardContent className="py-8 text-center text-muted-foreground">
                Právě není vypsaný žádný termín. Napište nám, rádi vás přidáme na waiting list nebo
                domluvíme firemní termín.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="hca-produkt-landing-upcoming-list">
              {product.upcomingTerminy.map((t) => {
                const volno = t.kapacitaMax - t.kapacitaObsazena;
                return (
                  <Card key={t.id} data-testid={`hca-produkt-landing-termin-${t.id}`}>
                    <CardContent className="grid gap-4 p-5 md:grid-cols-[1fr_auto] md:items-center">
                      <div className="flex items-start gap-3">
                        <Calendar className="h-5 w-5 text-primary" aria-hidden="true" />
                        <div>
                          <div className="font-semibold">
                            {formatTerminDate(t.datumOd, t.datumDo)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {nonEmpty(t.misto, misto)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row md:flex-col">
                        <div className="text-sm text-muted-foreground">
                          Volná místa:{" "}
                          <span className="font-semibold text-foreground">
                            {volno} z {t.kapacitaMax}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          asChild
                          data-testid={`hca-produkt-landing-register-button-${t.id}`}
                        >
                          <Link
                            to="/registrace/$terminId"
                            params={{ terminId: t.id }}
                            data-testid={`hca-produkt-landing-register-${t.id}`}
                          >
                            Přihlásit se
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              size="lg"
              className="h-12"
              onClick={openDiagnoza}
              data-testid="hca-produkt-landing-diagnoza-button"
            >
              Spustit diagnostiku
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-12"
              asChild
              data-testid="hca-produkt-landing-all-button"
            >
              <Link to="/terminy" data-testid="hca-produkt-landing-all-link">
                Všechny termíny
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        ref={faqRef}
        className="mx-auto w-full max-w-3xl px-4 py-12"
        data-testid="hca-produkt-landing-faq"
        aria-label="Časté dotazy"
      >
        <div className="mb-6 inline-flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-primary" aria-hidden="true" />
          <h2 className="text-2xl font-bold">Časté dotazy</h2>
        </div>
        <div className="divide-y divide-border rounded-lg border border-border">
          {FAQ.map((item) => (
            <details key={item.q} className="group p-4" data-testid="hca-produkt-landing-faq-item">
              <summary className="cursor-pointer list-none font-medium marker:content-none">
                <span className="flex items-center justify-between gap-3">
                  {item.q}
                  <span
                    className="text-muted-foreground transition-transform group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </span>
              </summary>
              <p className="mt-2 text-sm text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}
